import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const useScrollToAnchor = () => {
  const location = useLocation();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const observerRef = useRef<MutationObserver>();

  useEffect(() => {
    // Nettoie les références précédentes
    const cleanup = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };

    // Pas d'ancre dans l'URL
    if (!location.hash) {
      cleanup();
      return;
    }

    // Parse l'ID de l'ancre (gère les formats avec @)
    const anchorId = /@/.test(location.hash)
      ? location.hash.split('@')[1]
      : location.hash.substring(1);

    if (!anchorId) {
      cleanup();
      return;
    }

    // Fonction de scroll optimisée
    const scrollToElement = (): boolean => {
      const element = document.getElementById(anchorId);

      if (!element) return false;

      // Calcule l'offset pour éviter les headers fixes
      const headerOffset = 80; // Ajustez selon votre header
      const elementRect = element.getBoundingClientRect();
      const targetPosition = window.scrollY + elementRect.top - headerOffset;

      // Scroll fluide vers la position calculée
      window.scrollTo({
        top: Math.max(0, targetPosition),
        behavior: 'smooth',
      });

      return true;
    };

    // Tentative immédiate
    if (scrollToElement()) {
      return cleanup;
    }

    // Stratégie de retry avec délai progressif
    let retryCount = 0;
    const maxRetries = 5;

    const retryScroll = () => {
      if (scrollToElement() || retryCount >= maxRetries) {
        cleanup();
        return;
      }

      retryCount++;
      const delay = Math.min(200 * retryCount, 1000); // Max 1s
      timeoutRef.current = setTimeout(retryScroll, delay);
    };

    // Observer pour détecter les changements DOM
    const startObserver = () => {
      observerRef.current = new MutationObserver((mutations) => {
        // Vérifie si l'élément cible a été ajouté
        const hasTargetElement = mutations.some((mutation) =>
          Array.from(mutation.addedNodes).some((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              return (
                element.id === anchorId || element.querySelector(`#${anchorId}`)
              );
            }
            return false;
          }),
        );

        if (hasTargetElement && scrollToElement()) {
          cleanup();
        }
      });

      // Observer les changements dans le DOM
      observerRef.current.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: false,
      });

      // Timeout de sécurité pour l'observer
      timeoutRef.current = setTimeout(() => {
        cleanup();
        // Dernière tentative après timeout
        scrollToElement();
      }, 5000);
    };

    // Lance les deux stratégies en parallèle
    startObserver();
    retryScroll();

    // Cleanup au démontage
    return cleanup;
  }, [location.hash, location.pathname]);

  // Cleanup au démontage du composant
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);
};

export default useScrollToAnchor;
